import React, { useRef, useEffect } from 'react';

// wrapper/higher-order component for the Intersection Observer API
// see more here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API

// Named this component `IntObserver` so as to not clobber the global
// `IntersectionObserver` from the API itself

/**
 * IntObserver - A functional component wrapper for the Intersection Observer API
 * @param {object} props - Component props
 * @param {Function} props.callback - Required callback function for intersection observer
 * @param {React.ReactNode} props.children - Optional children to render
 * @param {object} props.options - Optional intersection observer options
 * @param {Element} props.root - Optional root element for intersection observer
 * @param {string} props.rootMargin - Optional root margin (defaults to '0px')
 * @param {number|Array<number>} props.threshold - Optional threshold value(s)
 * @returns {JSX.Element} Rendered component
 */
const IntObserver = ({
  callback,
  children = null,
  options = null,
  root = null,
  rootMargin = '0px',
  threshold = null,
}) => {
  const containerRef = useRef(null);
  const intersectionObserverRef = useRef(null);

  useEffect(() => {
    const opts = {
      root,
      rootMargin,
      threshold,
      ...options,
    };

    if (typeof callback === 'function') {
      // Create a safe callback wrapper to handle errors gracefully
      /**
       * Safe callback wrapper that handles errors gracefully
       * @param {IntersectionObserverEntry[]} entries - The intersection observer entries
       */
      const safeCallback = (entries) => {
        try {
          callback(entries);
        } catch (error) {
          // Log the error but don't break the component
          console.warn('IntObserver callback error:', error);
        }
      };

      const observer = new IntersectionObserver(safeCallback, opts);
      if (observer && typeof observer.observe === 'function' && containerRef.current) {
        intersectionObserverRef.current = observer;
        observer.observe(containerRef.current);
      }
    }

    // Cleanup function
    return () => {
      if (intersectionObserverRef.current && containerRef.current) {
        intersectionObserverRef.current.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="intersection-observer"
    >
      {children}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default IntObserver;
